'use client';

import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';

import { HomeGreeting } from '@/components/home/HomeGreeting';
import { WidgetGrid } from '@/components/home/WidgetGrid';
import { SpaceInvitations } from '@/components/spaces/SpaceInvitations';
import { AppHeaderConfig } from '@/contexts/AppHeaderContext';
import { useAccounts } from '@/hooks/queries/useAccounts';
import { useChatPage } from '@/hooks/useChatPage';
import { track } from '@/lib/telemetry';

// Mirrors the md: breakpoint that decides carousel vs grid in WidgetGrid — the
// event must describe the layout the user actually saw.
const CAROUSEL_MEDIA_QUERY = '(max-width: 767px)';

/**
 * Home ("Início") — the released widget-based homepage rendered at `/`.
 */
export default function HomePage() {
  const { user } = useUser();
  useChatPage('home');

  // `has_data` marks whether the user saw their own imported data — its first
  // `true` per user is the activation "aha" moment, so the view event waits for
  // the accounts query to settle instead of firing blind on mount.
  const { data: accounts, error: accountsError, isLoading: accountsLoading } = useAccounts();
  const viewTracked = useRef(false);

  useEffect(() => {
    if (viewTracked.current || accountsLoading) return;
    viewTracked.current = true;
    track('new_home.viewed', {
      layout: window.matchMedia(CAROUSEL_MEDIA_QUERY).matches ? 'carousel' : 'grid',
      has_data: !accountsError && (accounts?.length ?? 0) > 0,
    });
  }, [accountsLoading, accounts, accountsError]);

  const firstName = user?.firstName ?? user?.fullName?.split(' ')[0] ?? 'Você';

  return (
    <div data-onboarding-home className="flex flex-col gap-6">
      {/* The widgets are cycle-scoped and ignore the global period — the top
          bar's date filter would be a lie here. */}
      <AppHeaderConfig hidePeriodPicker />
      <HomeGreeting name={firstName} />
      <SpaceInvitations />
      <WidgetGrid />
    </div>
  );
}
